import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';
import { getDecryptedCookie } from '../utils/cryptoUtils';

const Step1 = ({ formData, handleInputChange, setStep, hospitalId, needsSessionStart = false, onStepChange }) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [inputErrors, setInputErrors] = useState({
    age: false,
    height: false,
    weight: false,
    gender: false,
  });
  const [sessionStarted, setSessionStarted] = useState(false);

  const ageRef = useRef(null);
  const heightRef = useRef(null);
  const weightRef = useRef(null);

  useEffect(() => {
    const { gender, age, height, weight } = formData;
    setIsFormValid(gender && age && height && weight);
  }, [formData]);

  useEffect(() => {
    sessionStorage.setItem('lastPage', 'Step1');
    
    // セッションリセットチェック
    const isCompletedSession = sessionStorage.getItem('session_completed') === 'true';
    const isPageRefresh = performance.navigation && performance.navigation.type === 1;
    
    // 完了したセッションやページリフレッシュの場合はセッションIDをクリア
    if (isCompletedSession || isPageRefresh) {
      console.log('Step1: セッションが完了しているかページリフレッシュが検出されました。セッションIDをクリアします。');
      sessionStorage.removeItem('session_id');
      sessionStorage.removeItem('session_completed');
      setSessionStarted(false);
    } else {
      // クリア条件に当てはまらない場合のみ既存セッションをチェック
      const existingSessionId = sessionStorage.getItem('session_id');
      
      if (existingSessionId) {
        // 既存のセッションIDがある場合は既にセッションが開始されているとみなす
        console.log('Step1: 既存のセッションIDを検出:', existingSessionId);
        setSessionStarted(true);
      } else {
        console.log('Step1: 既存のセッションIDがありません。needsSessionStartがtrueの場合は新しいセッションを開始します。');
        setSessionStarted(false);
      }
    }
  }, []);

  // セッションを開始する関数
  const startSession = async () => {
    if (!hospitalId) {
      console.error('Hospital ID is missing for session start');
      return false;
    }

    try {
      // 再度セッションIDの存在確認（非同期処理中に変更された可能性があるため）
      const existingSessionId = sessionStorage.getItem('session_id');
      // すでにセッションが開始されている場合は何もしない
      if (sessionStarted || existingSessionId) {
        console.log('Step1: セッションはすでに開始されています', existingSessionId);
        setSessionStarted(true); // 念のためステートを更新
        return true;
      }

      console.log('Step1: 新しいセッションを開始します...');
      // 一時的なIDを生成して設定（接続エラー時にも続行できるように）
      const tempSessionId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      try {
        // データ登録前に古いセッション関連データをクリア
        sessionStorage.removeItem('session_completed');
        
        const { data, error } = await supabase
          .from('sessions')
          .insert([{ 
            hospital_id: hospitalId
          }])
          .select('id');

        if (error) {
          console.error('Supabase session insert error:', error);
          // エラー時には一時的なIDを使用
          sessionStorage.setItem('session_id', tempSessionId);
          setSessionStarted(true);
          console.log('エラーのため一時的なセッションIDを使用:', tempSessionId);
          return true; // エラーがあってもユーザー体験のために続行
        }

        if (!data || data.length === 0) {
          console.error('セッションデータが返されませんでした');
          sessionStorage.setItem('session_id', tempSessionId);
          setSessionStarted(true);
          console.log('データ不足のため一時的なセッションIDを使用:', tempSessionId);
          return true;
        }

        const sessionId = data[0].id;
        sessionStorage.setItem('session_id', sessionId);
        setSessionStarted(true);
        console.log('Step1: セッション開始:', sessionId);
        return true;
      } catch (error) {
        console.error('セッション開始エラー:', error);
        // 例外発生時も一時的なIDを使用
        sessionStorage.setItem('session_id', tempSessionId);
        setSessionStarted(true);
        console.log('エラーのため一時的なセッションIDを使用:', tempSessionId);
        return true; // 例外があってもユーザー体験のために続行
      }
    } catch (error) {
      console.error('予期しないエラー:', error);
      return false;
    }
  };

  const handleInputChangeWithFocus = (e) => {
    const { name, value } = e.target;
    handleInputChange(e);

    if (name === 'gender' && value) {
      requestAnimationFrame(() => {
        ageRef.current.focus();
        ageRef.current.setSelectionRange(0, ageRef.current.value.length);
      });
    } else if (name === 'age' && value.length === 2) {
      requestAnimationFrame(() => {
        heightRef.current.focus();
        heightRef.current.setSelectionRange(0, heightRef.current.value.length);
      });
    } else if (name === 'height' && value.length >= 3) {
      requestAnimationFrame(() => {
        weightRef.current.focus();
        weightRef.current.setSelectionRange(0, weightRef.current.value.length);
      });
    }
  };

  const validateInputs = () => {
    const ageValue = parseInt(formData.age, 10);
    const heightValue = parseInt(formData.height, 10);
    const weightValue = parseInt(formData.weight, 10);

    const errors = {
      age: isNaN(ageValue) || ageValue < 40 || ageValue > 90 || formData.age.startsWith('0'),
      height: isNaN(heightValue) || heightValue < 100 || heightValue > 220 || formData.height.startsWith('0'),
      weight: isNaN(weightValue) || weightValue < 25 || weightValue > 125 || formData.weight.startsWith('0'),
      gender: !formData.gender,
    };
    setInputErrors(errors);

    const messages = new Set();
    if (errors.age) {
      messages.add('年齢が40歳から90歳の方がほねなびのご対象です。それ以外の方は受付にお声がけください。');
    }
    if (errors.height) {
      messages.add('身長は100cmから220cmの間で入力してください。');
    }
    if (errors.weight) {
      messages.add('体重は25kgから125kgの間で入力してください。');
    }
    if (errors.gender) {
      messages.add('性別を選択してください。');
    }
    setErrorMessages(Array.from(messages));

    return !errors.age && !errors.height && !errors.weight && !errors.gender;
  };

  const handleNextStep = async () => {
    if (validateInputs()) {
      // セッション開始が必要な場合は実行
      if (needsSessionStart && !sessionStarted) {
        try {
          const success = await startSession();
          if (!success) {
            console.error('セッション開始に失敗しました');
            // セッション開始に失敗しても続行
          } else if (onStepChange) {
            // セッション開始に成功したらonStepChangeを呼び出し
            onStepChange();
          }
          
          // targettingがfalseの医療機関の場合、Beforehand画面をスキップするため
          // osteoporosisTreatmentとboneDensityTestを明示的にnullに設定
          const hospitalInfo = JSON.parse(getDecryptedCookie('hospital_info') || '{}');
          const { data, error } = await supabase
            .from('hospitals')
            .select('targetting')
            .eq('id', hospitalId)
            .single();
            
          if (!error && data && data.targetting === false) {
            console.log('Step1: targetting=falseの医療機関なので、事前質問の値をnullに設定します');
            handleInputChange({
              target: {
                name: 'osteoporosisTreatment',
                value: null
              }
            });
            handleInputChange({
              target: {
                name: 'boneDensityTest',
                value: null
              }
            });
          }
        } catch (error) {
          console.error('セッション開始中に予期しないエラー:', error);
          // エラーが発生しても続行
        }
      } else if (onStepChange) {
        // セッション開始が不要な場合でもonStepChangeを呼び出し
        onStepChange();
      }
      setStep(3);
    }
  };

  const handleKeyDown = (e) => {
    // 数字入力の長さ制限はキープ（UIの使いやすさのため）
    const { name, value } = e.target;
    if (name === 'age' && value.length >= 2 && e.key !== 'Backspace') {
      e.preventDefault();
    } else if (name === 'height' && value.length >= 3 && e.key !== 'Backspace') {
      e.preventDefault();
    } else if (name === 'weight' && value.length >= 3 && e.key !== 'Backspace') {
      e.preventDefault();
    }
  };
  
  return (
    <div className="p-4 bg-blue-100 rounded-lg">
      <h2 className="common-title mb-4">ステップ 1</h2>
      <div className="mb-4 text-lg text-left">
        <label className="block mb-2 text-left">性別</label>
        <div className="flex items-center">
          <label className="mr-4">
            <input
              type="radio"
              name="gender"
              value="male"
              checked={formData.gender === 'male'}
              onChange={handleInputChangeWithFocus}
              className="mr-2 w-6 h-6"
            />
            男性
          </label>
          <label>
            <input
              type="radio"
              name="gender"
              value="female"
              checked={formData.gender === 'female'}
              onChange={handleInputChangeWithFocus}
              className="mr-2 w-6 h-6"
            />
            女性
          </label>
        </div>
      </div>
      <div className="mb-4 text-lg text-left">
        <label className="block mb-2 text-left">年齢</label>
        <input 
          type="tel" 
          name="age"
          value={formData.age} 
          onChange={handleInputChangeWithFocus}
          onKeyDown={handleKeyDown}
          inputMode="numeric"
          className={`w-full p-2 border rounded ${inputErrors.age ? 'bg-red-100' : ''}`}
          ref={ageRef}
        />
      </div>
      <div className="mb-4 text-lg text-left">
        <label className="block mb-2 text-left">身長 (cm)</label>
        <input 
          type="tel" 
          name="height"
          value={formData.height} 
          onChange={handleInputChangeWithFocus}
          onKeyDown={handleKeyDown}
          inputMode="numeric"
          className={`w-full p-2 border rounded ${inputErrors.height ? 'bg-red-100' : ''}`}
          ref={heightRef}
        />
      </div>
      <div className="mb-4 text-lg text-left">
        <label className="block mb-2 text-left">体重 (kg)</label>
        <input 
          type="tel" 
          name="weight"
          value={formData.weight} 
          onChange={handleInputChangeWithFocus}
          onKeyDown={handleKeyDown}
          inputMode="numeric"
          className={`w-full p-2 border rounded ${inputErrors.weight ? 'bg-red-100' : ''}`}
          ref={weightRef}
        />
      </div>
      {errorMessages.length > 0 && (
        <div className="text-red-600 text-right text-lg mb-4">
          {errorMessages.map((msg, index) => (
            <p key={index} className="mb-2">{msg}</p>
          ))}
        </div>
      )}
      <br />
      <div className="flex items-center justify-between">
        <button 
          onClick={() => setStep(0)}
          className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600 w-auto"
        >
          前の画面へ戻る
        </button>
        <button 
          onClick={handleNextStep} 
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={!isFormValid}
        >
          次の質問へ
        </button>
      </div>
    </div>
  );
};

export default Step1; 